import { msalInstance } from '@/auth/msal';
import { getTracker } from './tracker';

/**
 * Shell page-view logging.
 *
 * Faithful port of the legacy `UILoggerService.logPageView`
 * (`raptor-shell-ui/src/app/core/services/ui-logger.service.ts`):
 *   - gated on an authenticated session (`IsValidUserRequest` — legacy checks
 *     for an idtoken in sessionStorage; the React equivalent is an MSAL active
 *     account),
 *   - skips sensitive / empty routes (`IsValidPageRequest` — login,
 *     frameredirect, empty name),
 *   - derives a canonical parent-URL segment and maps `workspace` → `Apps`
 *     (`getParentURLSegement`) so telemetry dashboards stay consistent.
 *
 * Called from the router's `onResolved` subscription (router.tsx) instead of
 * AppInsights' `enableAutoRouteTracking`, which would log every raw URL
 * including `/login` and `/frameredirect`.
 */

/** Legacy `getParentURLSegement` + `workspace`→`Apps` mapping. */
function parentUrlSegment(href: string): string {
  const noQuery = href.split('?')[0];
  let parent: string;
  if (noQuery.indexOf('/apps') > -1) {
    parent = noQuery.split('/').slice(0, 6).join('/').split('(')[0];
  } else if (noQuery.indexOf('popup:apps') === -1) {
    parent = noQuery.split('/').slice(0, 4).join('/');
  } else {
    parent = noQuery;
  }
  return parent.replace('workspace', 'Apps');
}

/** Page name = last non-empty path segment; `/` → `home` (mounts the home MFE). */
function pageName(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length ? segments[segments.length - 1] : 'home';
}

/** Legacy `IsValidPageRequest` — never log login / frameredirect / empty names. */
function isValidPage(name: string): boolean {
  if (!name) return false;
  const lower = name.toLowerCase();
  return lower.indexOf('login') === -1 && lower.indexOf('frameredirect') === -1;
}

/**
 * Log a shell page view to AppInsights, applying the legacy gating + name
 * mapping. No-op when there is no active account or the route is sensitive.
 */
export function logPageView(pathname: string, href: string): void {
  if (!msalInstance.getActiveAccount()) return;
  const name = pageName(pathname);
  if (!isValidPage(name)) return;
  getTracker().pageView(name, parentUrlSegment(href), { pageType: 'Shell Pageview' });
}
